import { useMemo, useState } from 'react';
import type { CardCatalog } from '../types';
import { TopicCard } from './TopicCard';
import { useT } from '../i18n';

interface Props {
  catalog: CardCatalog;
  picked: number[];                  // ordered topic ids
  onChange: (ids: number[]) => void;
}

export function CardPicker({ catalog, picked, onChange }: Props) {
  const { t } = useT();
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState<number | 'all'>('all');

  const cardById = useMemo(() => new Map(catalog.cards.map((c) => [c.id, c])), [catalog]);
  const catById = useMemo(() => new Map(catalog.categories.map((c) => [c.id, c])), [catalog]);
  const pickedSet = new Set(picked);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog.cards.filter((c) => {
      if (activeCat !== 'all' && c.category_id !== activeCat) return false;
      if (q && !c.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [catalog.cards, search, activeCat]);

  // Group filtered cards by category for display
  const grouped = useMemo(() => {
    const m = new Map<number, typeof catalog.cards>();
    for (const c of filtered) {
      if (c.category_id == null) continue;
      if (c.active === false) continue;   // archived/legacy cards aren't selectable
      const arr = m.get(c.category_id) ?? [];
      arr.push(c);
      m.set(c.category_id, arr);
    }
    return Array.from(m.entries())
      .map(([catId, cards]) => ({ category: catById.get(catId)!, cards }))
      .filter((g) => g.category)
      .sort((a, b) => a.category.number - b.category.number);
  }, [filtered, catById]);

  const toggle = (id: number) => {
    if (pickedSet.has(id)) onChange(picked.filter((p) => p !== id));
    else onChange([...picked, id]);
  };

  return (
    <div className="card-picker">
      {/* picked chips with order */}
      <div className="card-picks">
        {picked.length === 0 && <span className="save-indicator">{t('cards.noPicks')}</span>}
        {picked.map((id, i) => {
          const c = cardById.get(id);
          if (!c) return null;
          const cat = c.category_id != null ? catById.get(c.category_id) : null;
          return (
            <TopicCard
              key={id}
              name={c.name}
              number={c.pdf_card_number}
              color={cat?.color}
              size="sm"
              order={i + 1}
              onRemove={() => toggle(id)}
            />
          );
        })}
      </div>

      {/* search + category filter chips */}
      <div className="card-toolbar">
        <input
          className="card-search"
          type="text"
          placeholder={t('cards.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="card-cat-chips">
          <button
            className={`card-cat-chip ${activeCat === 'all' ? 'card-cat-chip--active' : ''}`}
            onClick={() => setActiveCat('all')}
          >
            {t('cards.allCategories')}
          </button>
          {catalog.categories.map((cat) => (
            <button
              key={cat.id}
              className={`card-cat-chip ${activeCat === cat.id ? 'card-cat-chip--active' : ''}`}
              onClick={() => setActiveCat(activeCat === cat.id ? 'all' : cat.id)}
              title={cat.name}
            >
              <span className="card-cat-chip-dot" style={{ background: cat.color }} />
              <span className="card-cat-chip-num">{cat.number}</span>
              <span className="card-cat-chip-name">{cat.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* grid of card-boxes grouped by category */}
      <div className="card-grid-wrap">
        {grouped.length === 0 && <div className="empty-state">{t('cards.noMatches')}</div>}
        {grouped.map(({ category, cards }) => (
          <div key={category.id} className="card-grid-group">
            <div className="card-grid-group-title">
              <span className="card-cat-chip-dot" style={{ background: category.color }} />
              <span>{category.number}. {category.name}</span>
            </div>
            <div className="card-grid">
              {cards.map((c) => {
                const isPicked = pickedSet.has(c.id);
                return (
                  <TopicCard
                    key={c.id}
                    name={c.name}
                    number={c.pdf_card_number}
                    color={category.color}
                    size="sm"
                    selected={isPicked}
                    onClick={() => toggle(c.id)}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
